import {
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	PointerSensor,
	rectIntersection,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { isDragItem, isSortableData } from "./typeGuards";
import type { SortableData } from "./types";

type CrossListDndContextType = {
	activeItem: ReactNode | null;
	setActiveItem: (children: SortableData["children"]) => void;
};

const CrossListDndContext = createContext<CrossListDndContextType | null>(null);

export const useCrossListDnd = () => {
	const context = useContext(CrossListDndContext);
	if (!context) {
		throw new Error("useCrossListDnd must be used within CrossListDndProvider");
	}
	return context;
};

type CrossListDndProviderProps = {
	children: ReactNode;
	renderDragOverlay?: (activeItem: ReactNode) => ReactNode;
};

export function CrossListDndProvider({ children, renderDragOverlay }: CrossListDndProviderProps) {
	const [activeItem, setActiveItem] = useState<ReactNode | null>(null);
	const [skipDropAnimation, setSkipDropAnimation] = useState(false);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

	const handleSetActiveItem = useCallback((children: SortableData["children"]) => {
		setActiveItem(children);
	}, []);

	const handleDragStart = (event: DragStartEvent) => {
		const activeData = event.active?.data.current;

		setSkipDropAnimation(false);

		if (isSortableData(activeData)) {
			handleSetActiveItem(activeData.children);
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		const overData = over?.data.current;
		const activeData = active?.data.current;

		if (!overData || !isSortableData(activeData) || !isSortableData(overData)) {
			handleSetActiveItem(null);
			return;
		}

		// Check if item is being dropped
		if (isDragItem(activeData) && isDragItem(overData) && overData.isDroppable && overData.onDrop) {
			const successfulDrop = overData.onDrop(activeData, overData);

			if (successfulDrop) {
				setSkipDropAnimation(true);
			}
		}
		// Check if item is being sorted
		else if (overData.onSort) {
			overData.onSort(active.id, over.id);
		}

		handleSetActiveItem(null);
	};

	const handleDragCancel = () => {
		handleSetActiveItem(null);
	};

	const contextValue = useMemo(
		() => ({
			activeItem,
			setActiveItem: handleSetActiveItem,
		}),
		[activeItem, handleSetActiveItem],
	);

	return (
		<CrossListDndContext.Provider value={contextValue}>
			<DndContext
				sensors={sensors}
				collisionDetection={rectIntersection}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				{/* List of items */}
				{children}
				{/* Item that is being dragged */}
				<DragOverlay
					dropAnimation={skipDropAnimation ? null : undefined}
					style={{
						cursor: "grabbing",
					}}
				>
					{activeItem ? (
						<div style={{ opacity: 0.7 }}>
							{renderDragOverlay ? renderDragOverlay(activeItem) : activeItem}
						</div>
					) : null}
				</DragOverlay>
			</DndContext>
		</CrossListDndContext.Provider>
	);
}
